import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { $t, updateSurfacePalette } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import Lara from '@primeng/themes/lara';
import Nora from '@primeng/themes/nora';
import { AuthenticationService } from '../../core/services/authentication/authentication.service';
import {
  MeProfile,
  notifyConfig,
} from '../../core/services/profile/me-profile.model';
import { FirebaseConfigService } from '../../FirebaseConfig/firebaseConfig.service';
import { HomeApiService } from '../../modules/home/core/home-api.service';
import { FetchMeUser } from '../../modules/home/core/state/home.actions';
import { HomeState } from '../../modules/home/core/state/home.state';
import { DeviceService } from '../../services/device.service';
import { LayoutService } from '../service/layout.service';

declare type KeyOfType<T> = keyof T extends infer U ? U : never;

declare interface SurfacesType {
  name?: string;
  palette?: {
    0?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    950?: string;
  };
}
@Injectable({
  providedIn: 'root',
})
export class AppConfiguratorService {
  private readonly store = inject(Store);
  private readonly homeApiService = inject(HomeApiService);
  private readonly firebaseConfigService = inject(FirebaseConfigService);
  private readonly deviceService = inject(DeviceService);
  private translateService = inject(TranslateService);

  private me = this.store.selectSignal<MeProfile>(HomeState.getMe);
  private layoutService = inject(LayoutService);

  private readonly presets = {
    Aura,
    Lara,
    Nora,
  } as const;

  notificationPreference = computed(() => ({
    pushAll: this.me()?.push_all ?? true,
    notifyConfig: this.me()?.notifyConfig,
  }));
  showNotificationPopup = signal(false);
  private readonly NOTIFICATION_DISMISSED_KEY =
    'notification_permission_dismissed';
  private readonly NOTIFICATION_SNOOZED_KEY = 'notification_permission_snoozed';

  private readonly authService = inject(AuthenticationService);

  async updateNotificationPreference(
    key: string,
    value: boolean,
  ): Promise<boolean> {
    const userId = this.me()?.id;
    let config: Partial<notifyConfig> =
      this.notificationPreference().notifyConfig;

    if (key === 'pushAll') {
      const success = await this.handlePushAllToggle(value);

      if (!success) {
        return false;
      }

      Object.keys(config).forEach((key) => {
        config[key] = value;
      });
    } else {
      config = {
        [key]: value,
      };
    }

    if (userId) {
      return new Promise((resolve) => {
        this.homeApiService
          .updateNotifications(parseInt(userId), config)
          .subscribe({
            next: () => {
              this.store.dispatch(new FetchMeUser());
              resolve(true);
            },
            error: (err) => {
              console.error(
                '❌ Erro ao atualizar preferências no backend',
                err,
              );
              resolve(false);
            },
          });
      });
    }

    return false;
  }

  getNotificationValue(key: string): boolean {
    const config = this.notificationPreference()?.notifyConfig as Record<
      string,
      boolean
    >;
    return config?.[key] ?? true;
  }

  async checkNotificationStatus(): Promise<void> {
    if (!this.authService.isLogged()) return;

    const permission = this.deviceService.getNotificationPermission();
    const currentPushAllState = this.notificationPreference().pushAll;

    if (permission === 'default') {
      const permanentlyDismissed = localStorage.getItem(
        this.NOTIFICATION_DISMISSED_KEY,
      );
      const snoozedThisSession = sessionStorage.getItem(
        this.NOTIFICATION_SNOOZED_KEY,
      );

      if (!permanentlyDismissed && !snoozedThisSession) {
        this.showNotificationPopup.set(true);
      }
      return;
    }

    if (currentPushAllState === true && permission === 'denied') {
      setTimeout(
        async () => await this.syncNotificationStateWithBackend(false),
        1000,
      );
      await this.deviceService.disableNotifications();
    }
  }

  dismissNotificationPopup(permanent: boolean): void {
    if (permanent) {
      localStorage.setItem(this.NOTIFICATION_DISMISSED_KEY, 'true');
    } else {
      sessionStorage.setItem(this.NOTIFICATION_SNOOZED_KEY, 'true');
    }
    this.showNotificationPopup.set(false);
  }
  private async handlePushAllToggle(enable: boolean): Promise<boolean> {
    if (enable) {
      const success = await this.deviceService.enableNotifications();

      if (!success) {
        console.error('❌ Falha ao habilitar notificações');
        return false;
      }

      try {
        await this.firebaseConfigService.initializeAndGetToken(
          'user-toggle-on',
          true,
        );
      } catch (error) {
        console.error('⚠️ Erro ao reinicializar Firebase:', error);
      }

      return true;
    } else {
      const success = await this.deviceService.disableNotifications();

      if (!success) {
        console.warn('⚠️ Houve problemas ao desabilitar, mas continuando...');
      }

      return true;
    }
  }

  private async syncNotificationStateWithBackend(
    enable: boolean,
  ): Promise<void> {
    const userId = this.me()?.id;

    if (!userId) return;

    const config: Partial<notifyConfig> = {};
    const notifyConfig = this.notificationPreference().notifyConfig;

    Object.keys(notifyConfig).forEach((key) => {
      config[key] = enable;
    });

    return new Promise((resolve, reject) => {
      this.homeApiService
        .updateNotifications(parseInt(userId), config)
        .subscribe({
          next: () => {
            this.store.dispatch(new FetchMeUser());
            resolve();
          },
          error: (err) => {
            console.error('❌ Erro ao sincronizar com backend', err);
            reject(err);
          },
        });
    });
  }

  getMenuThemeOptions(isDark: boolean): { name: string; value: string }[] {
    if (isDark) {
      return [
        { name: 'Dark', value: 'dark' },
        { name: 'Primary', value: 'primary' },
      ];
    } else {
      return [
        { name: 'Light', value: 'light' },
        { name: 'Dark', value: 'dark' },
        { name: 'Primary', value: 'primary' },
      ];
    }
  }

  getPrimaryColors(): SurfacesType[] {
    const presetPalette =
      this.presets[
        this.layoutService.layoutConfig().preset as KeyOfType<
          typeof this.presets
        >
      ].primitive;

    const colors = [
      'emerald',
      'green',
      'lime',
      'orange',
      'amber',
      'yellow',
      'teal',
      'cyan',
      'sky',
      'blue',
      'indigo',
      'violet',
      'purple',
      'fuchsia',
      'pink',
      'rose',
    ];

    const palettes: SurfacesType[] = [{ name: 'noir', palette: {} }];

    colors.forEach((color) => {
      palettes.push({
        name: color,
        palette: presetPalette?.[
          color as KeyOfType<typeof presetPalette>
        ] as SurfacesType['palette'],
      });
    });

    return palettes;
  }

  getPresetExtension(primaryColor: string): any {
    const colors = this.getPrimaryColors();
    const color = colors.find((c) => c.name === primaryColor) || {};

    if (color.name === 'noir') {
      return {
        semantic: {
          primary: {
            50: '{zinc.50}',
            100: '{zinc.100}',
            200: '{zinc.200}',
            300: '{zinc.300}',
            400: '{zinc.400}',
            500: '{zinc.500}',
            600: '{zinc.600}',
            700: '{zinc.700}',
            800: '{zinc.800}',
            900: '{zinc.900}',
            950: '{zinc.950}',
          },
          colorScheme: {
            light: {
              primary: {
                color: '{primary.950}',
                contrastColor: '#ffffff',
                hoverColor: '{primary.800}',
                activeColor: '{primary.700}',
              },
              highlight: {
                background: '{primary.950}',
                focusBackground: '{primary.700}',
                color: '#ffffff',
                focusColor: '#ffffff',
              },
              surface: {
                0: '#ffffff',
                50: '{zinc.50}',
                100: '{zinc.100}',
                200: '{zinc.200}',
                300: '{zinc.300}',
                400: '{zinc.400}',
                500: '{zinc.500}',
                600: '{zinc.600}',
                700: '{zinc.700}',
                800: '{zinc.800}',
                900: '{zinc.900}',
                950: '{zinc.950}',
              },
            },
            dark: {
              primary: {
                color: '{primary.50}',
                contrastColor: '{primary.950}',
                hoverColor: '{primary.200}',
                activeColor: '{primary.300}',
              },
              highlight: {
                background: '{primary.50}',
                focusBackground: '{primary.300}',
                color: '{primary.950}',
                focusColor: '{primary.950}',
              },
              surface: {
                0: '#ffffff',
                50: '{zinc.50}',
                100: '{zinc.100}',
                200: '{zinc.200}',
                300: '{zinc.300}',
                400: '{zinc.400}',
                500: '{zinc.500}',
                600: '{zinc.600}',
                700: '{zinc.700}',
                800: '{zinc.800}',
                900: '{zinc.900}',
                950: '{zinc.950}',
              },
            },
          },
        },
      };
    } else {
      return {
        semantic: {
          primary: color.palette,
          colorScheme: {
            light: {
              primary: {
                color: '{primary.500}',
                contrastColor: '#ffffff',
                hoverColor: '{primary.600}',
                activeColor: '{primary.700}',
              },
              highlight: {
                background: '{primary.50}',
                focusBackground: '{primary.100}',
                color: '{primary.700}',
                focusColor: '{primary.800}',
              },
              surface: {
                0: 'color-mix(in srgb, {primary.900}, white 100%)',
                50: 'color-mix(in srgb, {primary.900}, white 95%)',
                100: 'color-mix(in srgb, {primary.900}, white 90%)',
                200: 'color-mix(in srgb, {primary.900}, white 80%)',
                300: 'color-mix(in srgb, {primary.900}, white 70%)',
                400: 'color-mix(in srgb, {primary.900}, white 60%)',
                500: 'color-mix(in srgb, {primary.900}, white 50%)',
                600: 'color-mix(in srgb, {primary.900}, white 40%)',
                700: 'color-mix(in srgb, {primary.900}, white 30%)',
                800: 'color-mix(in srgb, {primary.900}, white 20%)',
                900: 'color-mix(in srgb, {primary.900}, white 10%)',
                950: 'color-mix(in srgb, {primary.900}, white 0%)',
              },
            },
            dark: {
              primary: {
                color: '{primary.400}',
                contrastColor: '{surface.900}',
                hoverColor: '{primary.300}',
                activeColor: '{primary.200}',
              },
              highlight: {
                background:
                  'color-mix(in srgb, {primary.400}, transparent 84%)',
                focusBackground:
                  'color-mix(in srgb, {primary.400}, transparent 76%)',
                color: 'rgba(255,255,255,.87)',
                focusColor: 'rgba(255,255,255,.87)',
              },
              surface: {
                0: 'color-mix(in srgb, var(--surface-ground), white 100%)',
                50: 'color-mix(in srgb, var(--surface-ground), white 95%)',
                100: 'color-mix(in srgb, var(--surface-ground), white 90%)',
                200: 'color-mix(in srgb, var(--surface-ground), white 80%)',
                300: 'color-mix(in srgb, var(--surface-ground), white 70%)',
                400: 'color-mix(in srgb, var(--surface-ground), white 60%)',
                500: 'color-mix(in srgb, var(--surface-ground), white 50%)',
                600: 'color-mix(in srgb, var(--surface-ground), white 40%)',
                700: 'color-mix(in srgb, var(--surface-ground), white 30%)',
                800: 'color-mix(in srgb, var(--surface-ground), white 20%)',
                900: 'color-mix(in srgb, var(--surface-ground), white 10%)',
                950: 'color-mix(in srgb, var(--surface-ground), white 5%)',
              },
            },
          },
        },
      };
    }
  }

  updateColors(type: string, color: any): void {
    if (type === 'primary') {
      this.layoutService.layoutConfig.update((state) => ({
        ...state,
        primary: color.name,
      }));
    }

    this.applyTheme(type, color);
    this.layoutService.updateBodyBackground(color.name);
  }

  applyTheme(type: string, color: any): void {
    if (type === 'surface') {
      updateSurfacePalette(color.palette);
    }
  }

  changePreset(presetName: string): void {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      preset: presetName,
    }));

    const preset = this.presets[presetName as KeyOfType<typeof this.presets>];
    const primaryColor = this.layoutService.layoutConfig().primary;

    $t()
      .preset(preset)
      .preset(this.getPresetExtension(primaryColor))
      .use({ useDefaultOptions: true });
  }

  toggleDarkMode(): void {
    const currentDarkMode = this.layoutService.layoutConfig().darkTheme;

    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      darkTheme: !currentDarkMode,
    }));

    if (!currentDarkMode) {
      this.setMenuTheme('dark');
    }

    this.layoutService.updateBodyBackground(
      this.layoutService.layoutConfig().primary,
    );
  }

  setMenuTheme(theme: string): void {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      menuTheme: theme,
    }));
  }

  updateLanguage(languageCode: string): void {
    this.translateService.use(languageCode);
  }

  getCurrentLanguage(): string {
    return (
      this.translateService.currentLang || this.translateService.defaultLang
    );
  }

  getAvailableLanguages(): { name: string; value: string }[] {
    return [
      { name: 'PT', value: 'pt' },
      { name: 'EN', value: 'en' },
      { name: 'ES', value: 'es' },
    ];
  }
}
